import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Loader } from "@googlemaps/js-api-loader"
import { Place, PlaceGeocoding } from '../models/shared/Place';
import { MAP_RENDER_API_KEY } from '../utils/shared/constants';
import { generateMapPin } from '../utils/presentation';
import { Button } from 'react-bootstrap';
import { useText } from '../context/TextContext';
import { geocodingToPlace } from '../utils/geolocation';

const HEIGH = '200px';
const MAP_ELEMENT_ID = 'map-deal';

interface Props {
    readonly?: boolean
    placeGeocoding?: PlaceGeocoding
    placeBackgroundColor?: string
    orgGeocoding?: PlaceGeocoding
    onPlaceChange: (place: Place) => Promise<void>;
}
function MapDealView(props: Props) {
    const [selectedLocation, setSelectedLocation] = useState<PlaceGeocoding | null>(null);
    const infoWindowRef = useRef<any>(null);
    const infoWindowContentRef = useRef<HTMLDivElement | null>(null);
    const text = useText();

    useEffect(() => {
        if (!props.placeGeocoding) {
            return;
        }

        const asyncEffect = async () => {
            const loader = new Loader({ apiKey: MAP_RENDER_API_KEY });

            const mapElement = document.getElementById(MAP_ELEMENT_ID);
            if (!mapElement) {
                return;
            }

            mapElement.style.setProperty('min-height', HEIGH);
            mapElement.style.setProperty('resize', 'vertical');
            mapElement.classList.add("resizable")
            try {
                const { Map, InfoWindow } = await loader.importLibrary('maps');
                const { AdvancedMarkerElement, PinElement } = await loader.importLibrary('marker');
                const map = new Map(mapElement,
                    {
                        center: props.placeGeocoding,
                        zoom: 14,
                        mapId: `map`,
                        clickableIcons: false,
                    });

                if (!props.readonly) {
                    map.addListener('click', (event: any) => {
                        const location = {
                            lat: event.latLng.lat(),
                            lng: event.latLng.lng()
                        };

                        // Close previous info window if exists
                        if (infoWindowRef.current) {
                            infoWindowRef.current.close();
                        }

                        // Create container for React content
                        const contentDiv = document.createElement('div');
                        infoWindowContentRef.current = contentDiv;

                        const overlay = new InfoWindow({
                            content: contentDiv,
                            position: location,
                            headerDisabled: true,
                        })

                        infoWindowRef.current = overlay;
                        overlay.open({ map });

                        setSelectedLocation(location);
                    });
                }

                const faPin = generateMapPin(PinElement, { backgroundColor: props.placeBackgroundColor });

                new AdvancedMarkerElement({
                    position: props.placeGeocoding,
                    map: map,
                    content: faPin.element
                });

                if (props.orgGeocoding) {
                    const { LatLngBounds } = await loader.importLibrary('core');
                    const bounds = new LatLngBounds();

                    const orgPin = generateMapPin(PinElement, { type: 'home' });
                    new AdvancedMarkerElement({
                        position: props.orgGeocoding,
                        map: map,
                        content: orgPin.element
                    });

                    bounds.extend(props.placeGeocoding!);
                    bounds.extend(props.orgGeocoding);
                    map.fitBounds(bounds);
                }
            } catch (error) {
                console.error('Error loading map', error);
            }

        }

        asyncEffect();
    }, [props.placeGeocoding, props.orgGeocoding, props.placeBackgroundColor, props.readonly])

    const handleConfirmLocation = () => {
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
        if (selectedLocation) {
            const place = geocodingToPlace(selectedLocation);
            props.onPlaceChange(place);
        }
        infoWindowRef.current = null;
        infoWindowContentRef.current = null;
        setSelectedLocation(null);
    };

    const handleCancelLocation = () => {
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
        infoWindowRef.current = null;
        infoWindowContentRef.current = null;
        setSelectedLocation(null);
    };

    // React component to render inside InfoWindow
    const LocationConfirmContent = () => (
        <div>
            <p>{text.useThisLocation}</p>

            <div className='d-flex justify-content-end gap-2'>
                <Button size='sm' onClick={handleCancelLocation} variant='secondary'>
                    {text.cancel}
                </Button>
                <Button size='sm' onClick={handleConfirmLocation}>
                    {text.confirm}
                </Button>
            </div>
        </div>
    );

    return (
        <div style={{ position: 'relative' }}>
            <div id={MAP_ELEMENT_ID} />
            {selectedLocation && infoWindowContentRef.current &&
                createPortal(<LocationConfirmContent />, infoWindowContentRef.current)
            }
        </div>
    );
}

export default MapDealView
